import { loadStripe, Stripe } from '@stripe/stripe-js';
import { supabase } from '../supabaseClient';

// Initialize Stripe
let stripe: Stripe | null = null;

const initializeStripe = async () => {
  if (!stripe) {
    // You'll need to replace this with your actual Stripe publishable key
    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_your_key_here');
    stripe = await stripePromise;
  }
  return stripe;
};

export interface CreateCheckoutSessionParams {
  planCode: string;
  billingCycle: 'monthly' | 'yearly';
  trainerId: string;
  successUrl: string;
  cancelUrl: string;
  promoCode?: string;
}

export interface SubscriptionData {
  id: string;
  status: string;
  current_period_end: string;
  plan_code: string;
  plan_name: string;
  price_monthly: number;
  client_limit: number;
  features: any;
}

export class StripeService {
  // Create a checkout session for subscription
  static async createCheckoutSession(params: CreateCheckoutSessionParams) {
    try {
      console.log('StripeService: Creating checkout session with params:', params);
      
      const { data, error } = await supabase.functions.invoke('checkout-session', {
        body: {
          planCode: params.planCode,
          billingCycle: params.billingCycle,
          trainerId: params.trainerId,
          successUrl: params.successUrl,
          cancelUrl: params.cancelUrl,
          promoCode: params.promoCode
        }
      });

      console.log('StripeService: Supabase function response:', { data, error });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  // Redirect to Stripe checkout
  static async redirectToCheckout(sessionId: string) {
    try {
      console.log('StripeService: Redirecting to checkout with sessionId:', sessionId);
      
      const stripeInstance = await initializeStripe();
      if (!stripeInstance) {
        throw new Error('Stripe failed to initialize');
      }

      console.log('StripeService: Stripe initialized, calling redirectToCheckout...');
      
      const { error } = await stripeInstance.redirectToCheckout({
        sessionId
      });

      if (error) {
        console.error('StripeService: Stripe redirect error:', error);
        throw error;
      }
      
      console.log('StripeService: Redirect successful');
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  }

  // Create a billing portal session for subscription management
  static async createBillingPortalSession(trainerId: string, returnUrl: string) {
    try {
      const { data, error } = await supabase.functions.invoke('create-billing-portal-session', {
        body: {
          trainerId,
          returnUrl
        }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating billing portal session:', error);
      throw error;
    }
  }

  // Redirect to Stripe billing portal
  static async redirectToBillingPortal(url: string) {
    try {
      window.location.href = url;
    } catch (error) {
      console.error('Error redirecting to billing portal:', error);
      throw error;
    }
  }

  // Get current subscription for a trainer
  static async getCurrentSubscription(trainerId: string): Promise<SubscriptionData | null> {
    try {
      const { data, error } = await supabase
        .rpc('get_current_subscription', { trainer_uuid: trainerId });

      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('Error fetching current subscription:', error);
      throw error;
    }
  }

  // Cancel subscription
  static async cancelSubscription(trainerId: string) {
    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        body: { trainerId }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  // Reactivate subscription
  static async reactivateSubscription(trainerId: string) {
    try {
      const { data, error } = await supabase.functions.invoke('reactivate-subscription', {
        body: { trainerId }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      throw error;
    }
  }

  // Get subscription usage and limits
  static async getSubscriptionUsage(trainerId: string) {
    try {
      const { data, error } = await supabase
        .rpc('get_subscription_usage', { trainer_uuid: trainerId });

      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('Error fetching subscription usage:', error);
      throw error;
    }
  }

  // Check if trainer can add more clients
  static async canAddClient(trainerId: string): Promise<boolean> {
    try {
      const usage = await this.getSubscriptionUsage(trainerId);
      if (!usage) return false;

      const currentClients = usage.current_clients || 0;
      const clientLimit = usage.client_limit || 0;

      return currentClients < clientLimit;
    } catch (error) {
      console.error('Error checking client limit:', error);
      return false;
    }
  }

  // Get payment history
  static async getPaymentHistory(trainerId: string) {
    try {
      const { data, error } = await supabase
        .from('payment_history')
        .select('*')
        .eq('trainer_id', trainerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching payment history:', error);
      throw error;
    }
  }

  // Handle webhook events (for subscription updates)
  static async handleWebhookEvent(event: any) {
    try {
      const { data, error } = await supabase.functions.invoke('handle-stripe-webhook', {
        body: { event }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error handling webhook event:', error);
      throw error;
    }
  }
}

export default StripeService; 